import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export type BackendPage<T> = {
  content: T[];
  totalElements?: number;
  totalPages?: number;
  size?: number;
  number?: number;
};

export interface OpenBalanceRow {
  id: string; leaseId: string; tenantId: string; tenantName: string;
  propertyId: string; unitId: string; propertyName: string; unitLabel: string;
  chargeType: string; dueDate: string | null; amount: number; paidAmount: number;
  balance: number; status: string; overdue: boolean;
}
export interface OpenBalanceExposure {
  propertyId: string; propertyName: string; units: number; leases: number;
  total: number; overdueTotal: number; lateFeeTotal: number; oldestDueDate: string | null;
}
export interface OpenBalanceSummary {
  allCount: number; overdueCount: number; lateFeeCount: number; filteredCount: number;
  totalBalance: number; overdueTotal: number; lateFeeTotal: number;
  propertyExposure: OpenBalanceExposure[];
  filterOptions: { propertyId: string; propertyName: string; unitId: string; unitLabel: string }[];
}
export interface OpenBalanceFilters {
  page: number; size: number; type: string; search?: string; leaseId?: string | null;
  tenantId?: string | null; propertyId?: string | null; unitId?: string | null;
  from?: string | null; to?: string | null; sortBy: string; direction: string; includeSummary: boolean;
}
export interface OpenBalancesResponse extends BackendPage<OpenBalanceRow> {
  totalElements: number; number: number; size: number; summary: OpenBalanceSummary | null;
}

export type RentFrequencyApi = 'YEARLY' | 'MONTHLY' | 'WEEKLY' | 'DAILY' | 'ONE_TIME';

export type LeaseStatusApi =
  | 'DRAFT'
  | 'PENDING_SIGNATURE'
  | 'PENDING_APPROVAL'
  | 'ACTIVE'
  | 'NOTICE_GIVEN'
  | 'RENEWED'
  | 'TERMINATED'
  | 'EXPIRED'
  | 'FUTURE'
  | 'CANCELLED';

export type LeaseChargeTypeApi =
  | 'RENT'
  | 'SECURITY_DEPOSIT'
  | 'LATE_FEE'
  | 'GARAGE'
  | 'PARKING'
  | 'WATER'
  | 'SEWER'
  | 'TRASH'
  | 'GAS'
  | 'ELECTRIC'
  | 'INTERNET'
  | 'HOA'
  | 'PET_FEE'
  | 'STORAGE'
  | 'OTHER';

export type LeaseTemplateClauseCategoryApi =
  | 'RENT'
  | 'DEPOSIT'
  | 'UTILITIES'
  | 'LATE_FEES'
  | 'PETS'
  | 'OCCUPANTS'
  | 'MAINTENANCE'
  | 'ACCESS'
  | 'RENEWAL'
  | 'TERMINATION'
  | 'NOTICES'
  | 'INSURANCE'
  | 'SMOKING'
  | 'PARKING'
  | 'ADDENDA'
  | 'GOVERNING_LAW'
  | 'CUSTOM';

export type LeaseTemplateClauseDto = {
  id?: string | null;
  category: LeaseTemplateClauseCategoryApi;
  title: string;
  body: string;
  optionalClause?: boolean | null;
  enabledByDefault?: boolean | null;
  sortOrder?: number | null;
};

export type LeaseTemplateDto = {
  id?: string | null;
  organizationId?: string | null;
  name: string;
  description?: string | null;
  governingLaw?: string | null;
  agreementTitle?: string | null;
  agreementTitleAlignment?: 'LEFT' | 'CENTER' | 'RIGHT' | string | null;
  agreementTitleBold?: boolean | null;
  agreementTitleUnderlined?: boolean | null;
  openingText?: string | null;
  defaultTemplate?: boolean | null;
  active?: boolean | null;
  // Sent back on save so a save based on an outdated copy is refused instead of overwriting someone's changes.
  version?: number | null;
  clauses?: LeaseTemplateClauseDto[] | null;
};

export type LeaseDocumentGenerationRequest = {
  templateId?: string | null;
  lockVersion?: boolean | null;
  sendForSignature?: boolean | null;
  clauseOverrides?: LeaseTemplateClauseDto[] | null;
  customNotes?: string | null;
};

export type LeaseGeneratedDocumentStatusApi = 'DRAFT' | 'LOCKED' | 'SENT_FOR_SIGNATURE' | 'SIGNED' | 'VOID';

export type LeaseGeneratedDocumentDto = {
  id: string;
  leaseId?: string | null;
  templateId?: string | null;
  leaseFileId?: string | null;
  documentVersion?: number | null;
  status?: LeaseGeneratedDocumentStatusApi | null;
  title?: string | null;
  renderedText?: string | null;
  pdfUrl?: string | null;
  pdfPublicId?: string | null;
  lockedAt?: string | null;
  sentForSignatureAt?: string | null;
  signedAt?: string | null;
};

export type LateFeePolicyPayload = {
  enabled: boolean;
  graceDays: number;
  mode: 'FIXED_AMOUNT' | 'PERCENT_OF_RENT' | 'PERCENT_OF_OUTSTANDING' | 'FIXED_AMOUNT_PER_DAY' | 'PERCENT_OF_OUTSTANDING_PER_DAY';
  amount?: number | null;
  percent?: number | null;
  frequency: 'ONCE_PER_SCHEDULE' | 'DAILY' | 'EVERY_X_DAYS' | 'WEEKLY' | 'MONTHLY';
  max?: number | null;
};

export type OrganizationLeaseDefaultsDto = {
  id: string;
  defaultLateFeePolicy?: LateFeePolicyPayload | null;
  defaultLateFeeAppliesTo?: LeaseChargeTypeApi[] | null;
};

export type LeaseChargePayload = {
  chargeType: LeaseChargeTypeApi;
  label: string;
  amount: number;
  frequency: RentFrequencyApi;
  dueDay?: number | null;
  active: boolean;
  priority: number;
};

export type LateFeeOverridePayload = {
  chargeType: LeaseChargeTypeApi;
  label?: string | null;
  policy: LateFeePolicyPayload;
};

export type LeaseUpsertPayload = {
  startDate: string;
  endDate: string;
  unitId: string;
  propertyId: string;
  unitRent: number;
  unitDeposit: number;
  rentFrequency: RentFrequencyApi;
  rentDueDay: number;
  charges: LeaseChargePayload[];
  lateFeePolicy?: LateFeePolicyPayload | null;
  lateFeeAppliesTo?: LeaseChargeTypeApi[];
  lateFeeOverrides?: LateFeeOverridePayload[];
  primaryTenantId: string;
  status: LeaseStatusApi;
};

export type LeasePatchPayload = {
  startDate?: string;
  endDate?: string;
  unitRent?: number;
  unitDeposit?: number;
  rentFrequency?: RentFrequencyApi;
  rentDueDay?: number;
  charges?: LeaseChargePayload[];
  replaceCharges?: boolean;
  lateFeePolicy?: LateFeePolicyPayload | null;
  lateFeeAppliesTo?: LeaseChargeTypeApi[];
  lateFeeOverrides?: LateFeeOverridePayload[];
};

export type LeaseRenewPayload = {
  newStartDate: string;
  newEndDate: string;
  newRent: number;
  newDeposit: number;
  newFrequency: RentFrequencyApi;
  newRentDueDay?: number | null;
  charges?: LeaseChargePayload[];
  copyTenants: boolean;
  copyDocuments: boolean;
};

export type LeaseChargeResponseDto = {
  id: string;
  chargeType: LeaseChargeTypeApi;
  label?: string | null;
  amount?: number | null;
  frequency?: RentFrequencyApi | null;
  dueDay?: number | null;
  active?: boolean | null;
};

export type PartialPaymentPolicy = 'INHERIT' | 'ALLOW' | 'REQUIRE_FULL';

export type LeaseResponseDto = {
  displayStatus?: string;
  partialPaymentPolicy?: PartialPaymentPolicy;
  id: string;
  /** For a renewal: the lease it would replace. */
  previousLeaseId?: string | null;
  /** For a renewal offer: the last day the tenants can sign it. */
  offerExpiresOn?: string | null;
  /** Continuing a month at a time after its original end date. */
  monthToMonth?: boolean;
  /** Things staff should know about what they just did, e.g. an offer sent after the workspace's deadline. */
  warnings?: string[] | null;
  unitId?: string | null;
  propertyId?: string | null;
  startDate: string;
  endDate: string;
  rent?: number | null;
  deposit?: number | null;
  monthlyTotal?: number | null;
  rentFrequency?: RentFrequencyApi | null;
  rentDueDay?: number | null;
  status?: LeaseStatusApi | null;
  propertyName?: string | null;
  unitNumber?: string | null;
  primaryTenantId?: string | null;
  primaryTenantName?: string | null;
  contacts?: Array<{
    tenantId?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    fullName?: string | null;
    email?: string | null;
    role?: LeaseContactRoleApi | string | null;
  }> | null;
  charges?: LeaseChargeResponseDto[] | null;
  lateFeePolicy?: LateFeePolicyPayload | null;
  lateFeeAppliesTo?: LeaseChargeTypeApi[] | null;
};

export type LeaseContactRoleApi = 'PRIMARY' | 'CO_TENANT' | 'OCCUPANT' | 'GUARANTOR';

/** Someone who must sign the current agreement version. No portal account means no notification and no way to sign yet. */
export type LeaseSignerDto = {
  tenantId: string;
  name: string;
  email?: string | null;
  role: LeaseContactRoleApi;
  hasPortalAccount: boolean;
  signed: boolean;
  signedAt?: string | null;
};

export type LeaseContactPayload = {
  tenantId?: string | null;
  contactId?: string | null;
  role: LeaseContactRoleApi;
  replaceExistingPrimary?: boolean;
};

export type LeaseContactResponseDto = {
  tenantId?: string | null;
  contactId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: LeaseContactRoleApi | string | null;
};

export type LeaseFileResponseDto = {
  id: string;
  url?: string | null;
  contentType?: string | null;
  publicId?: string | null;
  uploadedAt?: string | null;
  current?: boolean | null;
};

export type RentScheduleSummaryDto = {
  nextDueDate?: string | null;
  pendingAmount?: number | null;
  paidAmount?: number | null;
  amountDue?: number | null;
  overdueAmount?: number | null;
  outstandingAmount?: number | null;
  oldestOverdueDate?: string | null;
  nextUpcomingDueDate?: string | null;
};

export type RentScheduleChargeStatusApi = 'PENDING' | 'PARTIALLY_PAID' | 'PAID' | 'OVERDUE' | 'CANCELLED' | 'VOIDED' | 'WAIVED' | string;

export type RentScheduleResponseDto = {
  id: string;
  dueDate?: string | null;
  amount?: number | string | null;
  paidAmount?: number | string | null;
  balance?: number | string | null;
  status?: RentScheduleChargeStatusApi | null;
  chargeType?: LeaseChargeTypeApi | null;
  parentScheduleId?: string | null;
  postedTransactionId?: string | null;
  /** Present when the charge covers only part of a month. */
  proration?: { days: number; daysInMonth: number; from: string; to: string } | null;
};

export type WaiveChargePayload = {
  waiveAmount: number;
  reason: string;
};

export type WaiveChargeResponseDto = {
  scheduleId: string;
  amountBefore?: number | null;
  paidAmount?: number | null;
  waivedAmountTotal?: number | null;
  waivedThisRequest?: number | null;
  balanceAfter?: number | null;
  status?: string | null;
  waivedBy?: string | null;
  waivedAt?: string | null;
  reason?: string | null;
};

export type RentBalanceActionPayload = {
  note?: string | null;
};

export type RentBalanceActionResponseDto = {
  id: string;
  actionType?: 'NOTE' | 'REMINDER' | string | null;
  note?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  delivered?: boolean | null;
};

export type TenantResponseDto = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

export type PropertyResponseDto = {
  id: string;
  name?: string | null;
  address?: string | null;
};

export type UnitResponseDto = {
  id: string;
  unitNumber?: string | null;
  rentAmount?: number | null;
  leaseOccupancyStatus?: string | null;
  activeLeaseId?: string | null;
  /** True while an approved application holds the unit; `holdExpiresAt` is when that hold ends. */
  held?: boolean | null;
  holdExpiresAt?: string | null;
};

@Injectable({ providedIn: 'root' })
export class LeaseService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiGatewayUrl}/property-service`;

  browseLeases(orgId: string, params: { page: number; size: number; q: string; tab: string; sort: string; direction: string; includeSummary: boolean; propertyIds?: string[] }): Observable<{ content: LeaseResponseDto[]; totalElements: number; number: number; summary: Record<string, number> | null }> {
    return this.http.get<{ content: LeaseResponseDto[]; totalElements: number; number: number; summary: Record<string, number> | null }>(`${this.base}/organizations/${orgId}/leases/browse`, { params, withCredentials: true });
  }

  listLeases(orgId: string, page = 0, size = 100): Observable<BackendPage<LeaseResponseDto>> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', 'startDate,desc');
    return this.http.get<BackendPage<LeaseResponseDto>>(`${this.base}/organizations/${orgId}/leases`, {
      params,
      withCredentials: true,
    });
  }

  listAllLeases(orgId: string): Observable<BackendPage<LeaseResponseDto>> {
    return this.listLeases(orgId, 0, 100).pipe(
      expand((page) => {
        const current = page.number ?? 0;
        const totalPages = page.totalPages ?? 1;
        return current + 1 < totalPages ? this.listLeases(orgId, current + 1, 100) : EMPTY;
      }),
      reduce((all, page) => ({
        ...page,
        number: 0,
        content: [...all.content, ...(page.content ?? [])],
      }), { content: [] } as BackendPage<LeaseResponseDto>),
    );
  }

  getLease(orgId: string, leaseId: string): Observable<LeaseResponseDto> {
    return this.http.get<LeaseResponseDto>(`${this.base}/organizations/${orgId}/leases/${leaseId}`, { withCredentials: true });
  }

  createLease(orgId: string, payload: LeaseUpsertPayload): Observable<LeaseResponseDto> {
    return this.http.post<LeaseResponseDto>(`${this.base}/organizations/${orgId}/leases`, payload, {
      withCredentials: true,
    });
  }

  updatePaymentSettings(orgId: string, leaseId: string, partialPaymentPolicy: PartialPaymentPolicy): Observable<LeaseResponseDto> {
    return this.http.put<LeaseResponseDto>(`${this.base}/organizations/${orgId}/leases/${leaseId}/payment-settings`,
      { partialPaymentPolicy }, { withCredentials: true });
  }

  updateLease(orgId: string, leaseId: string, payload: LeasePatchPayload, recalcSchedule = true): Observable<LeaseResponseDto> {
    const params = new HttpParams().set('recalcSchedule', String(recalcSchedule));
    return this.http.patch<LeaseResponseDto>(`${this.base}/organizations/${orgId}/leases/${leaseId}`, payload, {
      params,
      withCredentials: true,
    });
  }

  getRentScheduleSummary(orgId: string, leaseId: string): Observable<RentScheduleSummaryDto> {
    return this.http.get<RentScheduleSummaryDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/rent-schedule/summary`,
      { withCredentials: true },
    );
  }

  recalculateRentSchedule(orgId: string, leaseId: string): Observable<RentScheduleSummaryDto> {
    return this.http.post<RentScheduleSummaryDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/rent-schedule/recalculate`,
      {},
      { withCredentials: true },
    );
  }

  listOpenBalances(orgId: string, filters: OpenBalanceFilters): Observable<OpenBalancesResponse> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== null && value !== undefined && value !== '') params = params.set(key, String(value));
    }
    return this.http.get<OpenBalancesResponse>(`${this.base}/organizations/${orgId}/rent-balances/open`,
      { params, withCredentials: true });
  }

  listRentSchedule(orgId: string, leaseId: string, page = 0, size = 100): Observable<BackendPage<RentScheduleResponseDto>> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size))
      .set('sort', 'dueDate,asc');
    return this.http.get<BackendPage<RentScheduleResponseDto>>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/rent-schedule`,
      { params, withCredentials: true },
    );
  }

  waiveRentScheduleCharge(
    orgId: string,
    leaseId: string,
    scheduleId: string,
    payload: WaiveChargePayload,
  ): Observable<WaiveChargeResponseDto> {
    return this.http.post<WaiveChargeResponseDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/rent-schedule/items/${scheduleId}/waive`,
      payload,
      { withCredentials: true },
    );
  }

  saveRentBalanceNote(
    orgId: string,
    scheduleId: string,
    payload: RentBalanceActionPayload,
  ): Observable<RentBalanceActionResponseDto> {
    return this.http.post<RentBalanceActionResponseDto>(
      `${this.base}/organizations/${orgId}/rent-balances/schedules/${scheduleId}/notes`,
      payload,
      { withCredentials: true },
    );
  }

  sendRentBalanceReminder(
    orgId: string,
    scheduleId: string,
    payload: RentBalanceActionPayload,
  ): Observable<RentBalanceActionResponseDto> {
    return this.http.post<RentBalanceActionResponseDto>(
      `${this.base}/organizations/${orgId}/rent-balances/schedules/${scheduleId}/reminders`,
      payload,
      { withCredentials: true },
    );
  }

  listLeaseContacts(orgId: string, leaseId: string): Observable<LeaseContactResponseDto[]> {
    return this.http.get<LeaseContactResponseDto[]>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/contacts`,
      { withCredentials: true },
    );
  }

  upsertLeaseContact(orgId: string, leaseId: string, payload: LeaseContactPayload): Observable<LeaseResponseDto> {
    return this.http.post<LeaseResponseDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/contacts`,
      payload,
      { withCredentials: true },
    );
  }

  updateLeaseContact(orgId: string, leaseId: string, tenantId: string, payload: LeaseContactPayload): Observable<LeaseResponseDto> {
    return this.http.patch<LeaseResponseDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/contacts/${tenantId}`,
      payload,
      { withCredentials: true },
    );
  }

  removeLeaseContact(orgId: string, leaseId: string, participantId: string): Observable<LeaseResponseDto> {
    return this.http.delete<LeaseResponseDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/contacts/${participantId}`,
      { withCredentials: true },
    );
  }

  listLeaseFiles(orgId: string, leaseId: string): Observable<LeaseFileResponseDto[]> {
    return this.http.get<LeaseFileResponseDto[]>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/files`,
      { withCredentials: true },
    );
  }

  uploadLeaseFile(orgId: string, leaseId: string, file: File, makeCurrent = true): Observable<LeaseResponseDto> {
    const body = new FormData();
    body.append('lease', file, file.name);
    const params = new HttpParams().set('makeCurrentKey', 'lease');
    return this.http.post<LeaseResponseDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/files`,
      body,
      { params: makeCurrent ? params : new HttpParams(), withCredentials: true },
    );
  }

  terminateLease(orgId: string, leaseId: string, reason?: string): Observable<LeaseResponseDto> {
    let params = new HttpParams();
    const trimmedReason = reason?.trim();
    if (trimmedReason) {
      params = params.set('reason', trimmedReason);
    }

    return this.http.post<LeaseResponseDto>(`${this.base}/organizations/${orgId}/leases/${leaseId}/terminate`, null, {
      params,
      withCredentials: true,
    });
  }

  deleteLease(orgId: string, leaseId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}`,
      { withCredentials: true },
    );
  }

  sendLeaseForSignature(orgId: string, leaseId: string): Observable<LeaseResponseDto> {
    return this.http.post<LeaseResponseDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/send-signature`,
      null,
      { withCredentials: true },
    );
  }

  listLeaseSigners(orgId: string, leaseId: string): Observable<LeaseSignerDto[]> {
    return this.http.get<LeaseSignerDto[]>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/signers`,
      { withCredentials: true },
    );
  }

  appendLandlordSignature(
    orgId: string,
    leaseId: string,
    signatureName: string,
    initials?: string,
  ): Observable<LeaseResponseDto> {
    let params = new HttpParams().set('signatureName', signatureName.trim());
    const trimmedInitials = initials?.trim();
    if (trimmedInitials) {
      params = params.set('initials', trimmedInitials);
    }

    return this.http.post<LeaseResponseDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/landlord-signature`,
      null,
      { params, withCredentials: true },
    );
  }

  listLeaseTemplates(orgId: string): Observable<LeaseTemplateDto[]> {
    return this.http.get<LeaseTemplateDto[]>(`${this.base}/organizations/${orgId}/lease-templates`, {
      withCredentials: true,
    });
  }

  getDefaultLeaseTemplate(orgId: string): Observable<LeaseTemplateDto> {
    return this.http.get<LeaseTemplateDto>(`${this.base}/organizations/${orgId}/lease-templates/default`, {
      withCredentials: true,
    });
  }

  saveLeaseTemplate(orgId: string, payload: LeaseTemplateDto): Observable<LeaseTemplateDto> {
    return this.http.post<LeaseTemplateDto>(`${this.base}/organizations/${orgId}/lease-templates`, payload, {
      withCredentials: true,
    });
  }

  listGeneratedLeaseDocuments(orgId: string, leaseId: string): Observable<LeaseGeneratedDocumentDto[]> {
    return this.http.get<LeaseGeneratedDocumentDto[]>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/generated-documents`,
      { withCredentials: true },
    );
  }

  generateLeaseDocument(
    orgId: string,
    leaseId: string,
    payload: LeaseDocumentGenerationRequest = {},
  ): Observable<LeaseGeneratedDocumentDto> {
    return this.http.post<LeaseGeneratedDocumentDto>(
      `${this.base}/organizations/${orgId}/leases/${leaseId}/generated-documents`,
      payload,
      { withCredentials: true },
    );
  }

  /** Tenants' requests to renew this lease, newest first. */
  renewalRequests(orgId: string, leaseId: string): Observable<LeaseRenewalRequestDto[]> {
    return this.http.get<LeaseRenewalRequestDto[]>(`${this.base}/organizations/${orgId}/leases/${leaseId}/renewal-requests`, {
      withCredentials: true,
    });
  }

  /** Say no to a tenant's renewal request; the reason is shown to the tenant. */
  declineRenewalRequest(orgId: string, requestId: string, reason: string): Observable<LeaseRenewalRequestDto> {
    return this.http.post<LeaseRenewalRequestDto>(`${this.base}/organizations/${orgId}/renewal-requests/${requestId}/decline`, { reason }, {
      withCredentials: true,
    });
  }

  renewLease(orgId: string, leaseId: string, payload: LeaseRenewPayload): Observable<LeaseResponseDto> {
    return this.http.post<LeaseResponseDto>(`${this.base}/organizations/${orgId}/leases/${leaseId}/renew`, payload, {
      withCredentials: true,
    });
  }

  getOrganizationDefaults(orgId: string): Observable<OrganizationLeaseDefaultsDto> {
    return this.http.get<OrganizationLeaseDefaultsDto>(`${this.base}/organization/${orgId}`, {
      withCredentials: true,
    });
  }

  listTenants(orgId: string): Observable<BackendPage<TenantResponseDto>> {
    const params = new HttpParams().set('size', '500').set('sort', 'lastName,asc');
    return this.http.get<BackendPage<TenantResponseDto>>(`${this.base}/organizations/${orgId}/tenants`, {
      params,
      withCredentials: true,
    });
  }

  listProperties(orgId: string): Observable<BackendPage<PropertyResponseDto>> {
    const params = new HttpParams().set('size', '100').set('sort', 'name,asc');
    return this.http.get<BackendPage<PropertyResponseDto>>(`${this.base}/organizations/${orgId}/properties`, {
      params,
      withCredentials: true,
    });
  }

  listUnits(orgId: string, propertyId: string): Observable<BackendPage<UnitResponseDto>> {
    const params = new HttpParams().set('size', '200').set('sort', 'unitNumber,asc');
    return this.http.get<BackendPage<UnitResponseDto>>(
      `${this.base}/organizations/${orgId}/properties/${propertyId}/units`,
      { params, withCredentials: true },
    );
  }
}

export type LeaseRenewalRequestStatus = 'OPEN' | 'OFFERED' | 'ACCEPTED' | 'DECLINED' | 'WITHDRAWN' | 'EXPIRED' | 'OFFER_DECLINED';

/** A tenant asking to renew; the team answers by preparing a renewal (which answers it) or declining with a reason. */
export type LeaseRenewalRequestDto = {
  id: string;
  leaseId: string;
  status: LeaseRenewalRequestStatus;
  requestedTermMonths: number;
  requestedStartDate: string;
  requestedEndDate: string;
  note?: string | null;
  declineReason?: string | null;
  tenantName?: string | null;
  requestedAt?: string | null;
  respondedAt?: string | null;
  renewalLeaseId?: string | null;
};
